import { useMutation } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { useAtom } from 'jotai';

import { Recipient, StateResponse, UpdateRecipientRequestPayload } from 'api/api';

import { bootstrapConfig } from '~src/context/config';
import { apiInstance, useGlobalState, useGlobalStateUpdate } from '~src/global-state';

import { complementAddress, trimEmptyAdditionalData } from '~address-form/utils/format';

import { isSameAsDeliveryCheckboxCheckedAtom } from './context/state-store';
import { useUserId } from './utils/user-id';

export function useDeliveryAddressMutation() {
  const updateGlobalState = useGlobalStateUpdate();
  const globalState = useGlobalState();
  const [userId, saveUserId] = useUserId();
  const [billingAddressTheSameAsDelivery] = useAtom(isSameAsDeliveryCheckboxCheckedAtom);

  const mutationFn = async (userInfo: Recipient) => {
    const newAddress = {
      ...trimEmptyAdditionalData(complementAddress(userInfo, globalState.purchase_country)),
      country: globalState.purchase_country,
    };
    const payload: UpdateRecipientRequestPayload = {
      billing_address_the_same: billingAddressTheSameAsDelivery,
      locales: [globalState.locale],
      recipient: newAddress,
      save_for_next_time: true,
      user_id: userId,
    };
    const { data } = await apiInstance.v4.updateRecipientPost(
      globalState.checkout_session_id,
      payload
    );
    return data.state_response;
  };

  return useMutation<StateResponse | undefined, AxiosError<{ message?: string }>, Recipient>({
    mutationFn,
    mutationKey: ['address'],
    onSuccess: state_response => {
      if (state_response === undefined) {
        return;
      }
      void updateGlobalState(state_response);
      if (!bootstrapConfig.externalAddressBookProvider) {
        saveUserId();
      }
    },
  });
}
